import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    getPaymentStatus,
    getFirm,
    setFirmSession,
    getMeta,
} from "@/lib/api";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2500;

export default function CheckoutResult() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling"); // polling | paid | expired | error
    const [tx, setTx] = useState(null);
    const [meta, setMeta] = useState(null);
    const pollsRef = useRef(0);

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
    }, []);

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let timer;
        const poll = async () => {
            pollsRef.current += 1;
            try {
                const r = await getPaymentStatus(sessionId);
                setTx(r);
                if (r.payment_status === "paid") {
                    setState("paid");
                    // refresh firm session
                    if (r.firm_id) {
                        try {
                            const f = await getFirm(r.firm_id);
                            setFirmSession(f);
                        } catch (err) {
                            // Non-fatal: payment is still confirmed; firm refresh just failed
                            console.error("Firm session refresh failed after payment:", err);
                        }
                    }
                    return;
                }
                if (r.status === "expired") {
                    setState("expired");
                    return;
                }
                if (pollsRef.current >= MAX_POLLS) {
                    setState("error");
                    return;
                }
                timer = setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                if (pollsRef.current >= MAX_POLLS) {
                    setState("error");
                    return;
                }
                timer = setTimeout(poll, POLL_INTERVAL_MS);
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[900px] mx-auto px-6 py-24" data-testid="checkout-result">
                {state === "polling" && (
                    <div className="border-2 border-white/20 p-16 text-center" data-testid="checkout-polling">
                        <Loader2 size={32} className="animate-spin mx-auto text-[#FF3B30]" />
                        <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mt-6">
                            CONFIRMING PAYMENT WITH STRIPE
                        </div>
                        <p className="font-sans text-sm text-white/50 mt-3">
                            Please wait while we verify. This usually takes a few seconds.
                        </p>
                    </div>
                )}

                {state === "paid" && tx && (() => {
                    const purpose = (tx.metadata || {}).purpose || "";
                    const caseId = (tx.metadata || {}).case_id;
                    let title = "Payment confirmed.";
                    let blurb = "Your purchase is complete.";
                    let backTo = "/firm-portal";
                    let backLabel = "GO TO FIRM PORTAL";
                    if (purpose === "hpm_firm_subscription_month") {
                        title = `Tier ${(tx.tier || "").toUpperCase()} active.`;
                        blurb = "Your firm is now upgraded. Lead limit and feature set updated immediately.";
                    } else if (purpose === "hpm_lead_ppl") {
                        title = "Lead unlocked.";
                        blurb = "The lead is now in your firm's claimed list. Open it any time to view the full 10-bot analysis.";
                        backTo = caseId ? `/hpm-analysis/${caseId}` : "/firm-portal";
                        backLabel = "VIEW THE CASE";
                    } else if (purpose === "hpm_user_product") {
                        title = "Document ready.";
                        blurb = "Your AI-generated legal document has been delivered to the case page.";
                        backTo = caseId ? `/hpm-analysis/${caseId}` : "/hpm-dashboard";
                        backLabel = "OPEN MY CASE";
                    }
                    return (
                    <div
                        className="border-2 border-white p-12 text-center"
                        data-testid="checkout-success"
                    >
                        <CheckCircle2 size={48} className="text-white mx-auto" />
                        <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mt-6 mb-2">
                            PAYMENT CONFIRMED
                        </div>
                        <h1 className="font-anton text-5xl sm:text-7xl uppercase leading-none">
                            <span className="text-[#FF3B30]">{title}</span>
                        </h1>
                        <p className="font-sans text-white/70 mt-6 max-w-xl mx-auto">
                            {blurb}
                        </p>
                        <div className="border-2 border-white/20 p-4 my-8 inline-block">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                AMOUNT PAID
                            </div>
                            <div className="font-anton text-4xl text-[#FF3B30] mt-1">
                                ${(tx.amount || 0).toFixed(2)}{" "}
                                <span className="text-white/30 text-base">USD</span>
                            </div>
                        </div>
                        <div>
                            <Link
                                to={backTo}
                                className="btn-brutal btn-brutal-red"
                                data-testid="checkout-continue"
                            >
                                {backLabel} <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                    );
                })()}

                {state === "expired" && (
                    <div
                        className="border-2 border-[#FF3B30] p-12 text-center"
                        data-testid="checkout-expired"
                    >
                        <XCircle size={48} className="text-[#FF3B30] mx-auto" />
                        <div className="font-anton text-3xl uppercase mt-4">Session expired.</div>
                        <p className="font-sans text-white/70 mt-3">
                            Your checkout session expired before completion. Please try again.
                        </p>
                        <Link to="/firm-portal" className="btn-brutal mt-6">
                            BACK TO PORTAL
                        </Link>
                    </div>
                )}

                {state === "error" && (
                    <div
                        className="border-2 border-[#FF3B30] p-12 text-center"
                        data-testid="checkout-error"
                    >
                        <XCircle size={48} className="text-[#FF3B30] mx-auto" />
                        <div className="font-anton text-3xl uppercase mt-4">
                            Could not verify payment.
                        </div>
                        <p className="font-sans text-white/70 mt-3">
                            We did not receive confirmation. If you completed payment, refresh in a moment.
                            Otherwise return to the portal and try again.
                        </p>
                        <Link to="/firm-portal" className="btn-brutal mt-6">
                            BACK TO PORTAL
                        </Link>
                    </div>
                )}
            </div>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
